import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
} from "@angular/core";

export type ToggleSwitchColorVariant = "default" | "success";
export type ToggleSwitchAppearance = "default" | "neumorphic";

@Component({
  selector: "toggle-switch",
  templateUrl: "toggle-switch.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToggleSwitchComponent {
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() isEnabled = false;
  /** `success` = green (theme `success-600`); `default` = brand primary. */
  @Input() colorVariant: ToggleSwitchColorVariant = "default";
  /**
   * `neumorphic` = DS 2.1 switch (52x32, sunken input track, raised knob, action-primary when on);
   * it ignores `colorVariant`.
   */
  @Input() appearance: ToggleSwitchAppearance = "default";
  /** Blocks click and keyboard toggling and drops the switch out of the tab order. */
  @Input() disabled = false;
  /** Accessible name when no visible label is associated with the switch. */
  @Input() ariaLabel: string | null = null;
  @Output() onClick: EventEmitter<boolean> = new EventEmitter<boolean>();

  get neumorphic(): boolean {
    return this.appearance === "neumorphic";
  }

  // preventDefault stops Space scrolling the page; stopPropagation keeps the key from also
  // activating a keyboard-activatable parent (nav-panel rows carry routerLink on the host).
  onToggleKey(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if ((event as KeyboardEvent).repeat) {
      return;
    }
    this.onToggleClick();
  }

  onToggleClick() {
    if (this.disabled) {
      return;
    }
    this.toggle();
    this.onClick.emit(this.isEnabled);
  }

  toggle() {
    this.isEnabled = !this.isEnabled;
  }

  /**
   * The switch flips itself on click, ahead of the parent. When the parent then keeps the old
   * value (a cancelled confirmation, a failed request), its [isEnabled] binding doesn't change, so
   * nothing flips the switch back - the parent calls this to show the real state again. Keeps the
   * element (and keyboard focus on it), unlike re-creating the switch.
   */
  resync(isEnabled: boolean) {
    this.isEnabled = isEnabled;
    this.cdr.markForCheck();
  }
}
